/**
 * Pure pixel and color operations for the headless tooling layer.
 * No DOM dependencies; unit-tested in node.
 */

export interface PixelImage {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  if (max === min) return [0, 0, lightness];
  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let hue: number;
  if (max === red) hue = ((green - blue) / delta + (green < blue ? 6 : 0)) / 6;
  else if (max === green) hue = ((blue - red) / delta + 2) / 6;
  else hue = ((red - green) / delta + 4) / 6;
  return [hue, saturation, lightness];
}

function hueChannel(p: number, q: number, t: number): number {
  let value = t;
  if (value < 0) value += 1;
  if (value > 1) value -= 1;
  if (value < 1 / 6) return p + (q - p) * 6 * value;
  if (value < 1 / 2) return q;
  if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6;
  return p;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const value = Math.round(l * 255);
    return [value, value, value];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hueChannel(p, q, h + 1 / 3) * 255),
    Math.round(hueChannel(p, q, h) * 255),
    Math.round(hueChannel(p, q, h - 1 / 3) * 255),
  ];
}

/** Inverts RGB channels in place; alpha is untouched. */
export function invertImage(image: PixelImage): void {
  const { data } = image;
  for (let index = 0; index < data.length; index += 4) {
    data[index] = 255 - (data[index] ?? 0);
    data[index + 1] = 255 - (data[index + 1] ?? 0);
    data[index + 2] = 255 - (data[index + 2] ?? 0);
  }
}

/** Rotates the hue of every pixel in place; alpha is untouched. */
export function hueShiftImage(image: PixelImage, degrees: number): void {
  const { data } = image;
  const shift = ((degrees % 360) + 360) % 360 / 360;
  if (shift === 0) return;
  for (let index = 0; index < data.length; index += 4) {
    const [h, s, l] = rgbToHsl(data[index] ?? 0, data[index + 1] ?? 0, data[index + 2] ?? 0);
    const [r, g, b] = hslToRgb((h + shift) % 1, s, l);
    data[index] = r;
    data[index + 1] = g;
    data[index + 2] = b;
  }
}

function parseHex(hex: string): [number, number, number] | null {
  const value = hex.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(value)) {
    const [r, g, b] = value;
    return [parseInt(`${r}${r}`, 16), parseInt(`${g}${g}`, 16), parseInt(`${b}${b}`, 16)];
  }
  if (/^[0-9a-fA-F]{6}$/.test(value)) {
    return [parseInt(value.slice(0, 2), 16), parseInt(value.slice(2, 4), 16), parseInt(value.slice(4, 6), 16)];
  }
  return null;
}

/** Formats RGB channels (0–255) as a lowercase hex color. */
export function rgbToHex(r: number, g: number, b: number): string {
  const channel = (value: number) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

const formatHex = rgbToHex;

/** Inverts a hex color; returns the input unchanged when it cannot be parsed. */
export function invertHex(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  return formatHex(255 - rgb[0], 255 - rgb[1], 255 - rgb[2]);
}

/** Rotates the hue of a hex color; returns the input unchanged when it cannot be parsed. */
export function hueShiftHex(hex: string, degrees: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const shift = ((degrees % 360) + 360) % 360 / 360;
  const [r, g, b] = hslToRgb((h + shift) % 1, s, l);
  return formatHex(r, g, b);
}
